/**
 * UI Types - Tables
 *
 * Types related to table components and table state management
 */

import type { QuickFillItem } from '../domain';

// =============================================================================
// Table Column Types
// =============================================================================

/**
 * Defines a table column configuration
 */
export interface TableColumn {
  /** Column data key */
  key: keyof QuickFillItem;
  /** Display label */
  label: string;
  /** Column width (CSS value) */
  width?: string | number;
  /** Whether the column is editable */
  editable?: boolean;
}

// =============================================================================
// Table History Types
// =============================================================================

/**
 * Represents a cell change for undo/redo
 */
export interface CellChange {
  /** Previous value */
  oldValue: string;
  /** New value */
  newValue: string;
}

/**
 * History state for undo/redo functionality
 */
export interface HistoryState {
  /** Data snapshot */
  data: QuickFillItem[];
  /** Map of edited cells */
  editedCells: Record<string, boolean>;
}

// =============================================================================
// Table Constants
// =============================================================================

/**
 * Maps display labels to data keys
 * Used for column visibility based on selected properties
 */
export const COLUMN_LABEL_TO_KEY: Record<string, keyof QuickFillItem> = {
  'SKEY': 'SKEY',
  'OBJECT-TYPE': 'OBJECT-TYPE',
  'End1': 'End1',
  'End2': 'End2',
  'End3': 'End3',
  'End4': 'End4',
  'Piping Spec': 'PIPING-SPEC',
  'PIPING-SPEC': 'PIPING-SPEC',
  'System Grouping': 'SYSTEM-GROUPING',
  'SYSTEM-GROUPING': 'SYSTEM-GROUPING',
  'Material': 'MATERIAL',
  'MATERIAL': 'MATERIAL',
  'Design Standard': 'DESIGN-STD',
  'DESIGN-STD': 'DESIGN-STD',
  'Pressure Class': 'PRESSURE-CLASS',
  'PRESSURE-CLASS': 'PRESSURE-CLASS',
  'Insulation Spec': 'INSULATION-SPEC',
  'INSULATION-SPEC': 'INSULATION-SPEC',
  'Insulation Thickness': 'INSULATION-THICKNESS',
  'INSULATION-THICKNESS': 'INSULATION-THICKNESS',
  'Tracing Spec': 'TRACING-SPEC',
  'TRACING-SPEC': 'TRACING-SPEC',
  'Item Description': 'ITEM-DESCRIPTION',
  'ITEM-DESCRIPTION': 'ITEM-DESCRIPTION',
};

/**
 * Default columns when no data is available
 */
export const DEFAULT_COLUMNS: (keyof QuickFillItem)[] = [
  'Name', 'SKEY', 'OBJECT-TYPE', 'End1', 'End2', 'End3', 'End4',
  'PIPING-SPEC', 'SYSTEM-GROUPING', 'MATERIAL', 'DESIGN-STD',
  'PRESSURE-CLASS', 'INSULATION-SPEC', 'INSULATION-THICKNESS',
  'TRACING-SPEC', 'ITEM-DESCRIPTION'
];

/**
 * Options for specific columns
 */
export const COLUMN_OPTIONS: Record<string, { value: string; label: string }[]> = {
  'SKEY': [
    { value: 'Op', label: 'Op' },
    { value: 'O9SP', label: 'O9SP' },
    { value: 'DUCK', label: 'DUCK' },
    { value: 'EL??', label: 'EL??' },
    { value: 'FLF', label: 'FLF' },
  ],
  'OBJECT-TYPE': [
    { value: 'INSTRUMENT', label: 'INSTRUMENT' },
    { value: 'SUPPORT', label: 'SUPPORT' },
    { value: 'ELBOW', label: 'ELBOW' },
    { value: 'FLANGE', label: 'FLANGE' },
    { value: 'VALVE', label: 'VALVE' },
  ],
  'END_TYPE': [
    { value: 'FL', label: 'FL' },
    { value: 'BV', label: 'BV' },
    { value: 'WF', label: 'WF' },
    { value: 'UNDEFINED_ET', label: 'UNDEFINED_ET' },
  ],
};

/**
 * Check if column is an end type column
 */
export const isEndTypeColumn = (column: string): boolean => {
  return ['End1', 'End2', 'End3', 'End4'].includes(column);
};
